import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

const SeverityDistributionChart = ({ interval, apiBaseUrl }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Color mapping for severity levels
  const SEVERITY_COLORS = {
    'Critical': '#e53e3e',
    'High': '#f6ad55',
    'Medium': '#feca57',
    'Low': '#48bb78',
    'Unknown': '#a0aec0',
  };

  useEffect(() => {
    fetchSeverityDistribution();
  }, [interval]);

  const fetchSeverityDistribution = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${apiBaseUrl}/chat_safety/alert_severity_distribution/${interval}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch severity distribution data');
      }

      const result = await response.json();
      
      // Transform the data
      const transformedData = result
        .map(item => ({
          severity: item[0] || 'Unknown',
          count: item[1] || 0,
          color: SEVERITY_COLORS[item[0]] || SEVERITY_COLORS['Unknown'],
        }))
        .filter(item => item.count > 0); // Only show categories with data

      setData(transformedData);
    } catch (err) {
      console.error('Error fetching severity distribution:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const total = data.reduce((sum, item) => sum + item.count, 0);
      const percentage = ((payload[0].value / total) * 100).toFixed(1);
      
      return (
        <div style={{
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          padding: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}>
          <p style={{
            margin: '0 0 6px 0',
            fontWeight: '600',
            color: '#2d3748',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <span style={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              background: payload[0].payload.color,
            }}></span>
            {payload[0].payload.severity}
          </p>
          <p style={{
            margin: '0',
            fontSize: '12px',
            color: '#4a5568',
          }}>
            Count: <strong style={{ color: '#1a202c' }}>{payload[0].value}</strong>
          </p>
          <p style={{
            margin: '4px 0 0 0',
            fontSize: '12px',
            color: '#4a5568',
          }}>
            Percentage: <strong style={{ color: '#1a202c' }}>{percentage}%</strong>
          </p>
        </div>
      );
    }
    return null;
  };

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    // Only show label if percentage is >= 5%
    if (percent < 0.05) return null;

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        style={{ fontSize: '13px', fontWeight: '600', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const CustomLegend = ({ payload }) => {
    const total = data.reduce((sum, item) => sum + item.count, 0);
    
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: '16px',
        marginTop: '20px',
      }}>
        {payload.map((entry, index) => (
          <div
            key={`legend-${index}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '12px',
              color: '#4a5568',
            }}
          >
            <span style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              background: entry.color,
              flexShrink: 0,
            }}></span>
            <span style={{ fontWeight: '500' }}>
              {entry.value}: <strong style={{ color: '#1a202c' }}>
                {data[index].count}
              </strong>
            </span>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px',
        color: '#718096',
        fontSize: '14px',
        gap: '12px',
      }}>
        <div style={{
          width: '32px',
          height: '32px',
          border: '3px solid #e2e8f0',
          borderTopColor: '#667eea',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }}></div>
        <span>Loading severity distribution...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px',
        color: '#e53e3e',
        fontSize: '14px',
      }}>
        <p>Error loading distribution: {error}</p>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px',
        color: '#718096',
        fontSize: '14px',
      }}>
        <p>No severity data available for this period</p>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%', minHeight: '300px' }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="45%"
            labelLine={false}
            label={renderCustomLabel}
            outerRadius={100}
            fill="#8884d8"
            dataKey="count"
            animationBegin={0}
            animationDuration={800}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend content={<CustomLegend />} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default SeverityDistributionChart;